import network from './route-network.json';

export const visitorFilters = [
  { id: 'all', label: 'All types' },
  { id: 'food', label: 'Cafés & food' },
  { id: 'toilets', label: 'Toilets' },
  { id: 'play', label: 'Play & animals' },
  { id: 'shops', label: 'Shops' },
];

const groups = {
  food: ['cafe', 'bistro', 'farmshop'],
  toilets: ['toilets'],
  play: ['playground', 'animal-park', 'stuart', 'dave'],
  shops: ['retail', 'farmshop', 'duchess', 'petshop', 'retail-info', 'crafts'],
};

export function filterPlaces(list, query, category, filter) {
  const q = query.trim().toLocaleLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  return list.filter((p) => {
    const text = `${p.name} ${p.kind} ${p.description} ${p.id === 'toilets' ? 'WC bathroom restroom' : ''} ${
      p.category === 'retail' ? 'Engine Yard retail shopping' : ''
    }`
      .toLocaleLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    return (
      (q || filter !== 'all' || category === 'all' || p.category === category) &&
      (!q || text.includes(q)) &&
      (filter === 'all' || groups[filter].includes(p.id))
    );
  });
}

const ownerIds = new Set([
  'tickets',
  'toilets',
  'cafe',
  'playground',
  'retail',
  'farmshop',
  'wellness',
  'duchess',
  'petshop',
  'bistro',
  'retail-info',
  'vale-house',
  'crafts',
  'animal-park',
  'stuart',
  'dave',
  'dairy',
  'dog-park',
]);

export function verification(id) {
  return {
    label: ownerIds.has(id) ? 'Owner-identified location' : 'Mapped location',
    detail: ownerIds.has(id)
      ? 'Identified from the owner’s instructions. Exact entrances and routes still need an approved plan.'
      : 'Based on mapped reference data. Not checked against an approved estate plan.',
    approved: false,
  };
}

export function canWalk(tags, avoidSteps = false) {
  const access = tags.foot ?? tags.access;
  return (
    !['no', 'private', 'customers', 'delivery'].includes(access ?? '') &&
    !['motorway', 'trunk', 'construction', 'proposed'].includes(tags.highway ?? '') &&
    !(avoidSteps && tags.highway === 'steps')
  );
}

function graph(avoidSteps) {
  const nodes = [];
  const ids = new Map();
  function node(id, point) {
    let i = ids.get(id);
    if (i === undefined) {
      i = nodes.length;
      ids.set(id, i);
      nodes.push({ point, edges: [] });
    }
    return i;
  }
  network.ways.forEach((way, w) => {
    if (!canWalk(way.tags || {}, avoidSteps)) return;
    for (let i = 0; i < way.points.length - 1; i++) {
      const a = way.points[i];
      const b = way.points[i + 1];
      if ([a, b].some(([x, n]) => x < -335 || x > 300 || n < -355 || n > 360)) continue;
      const distance = Math.hypot(b[0] - a[0], b[1] - a[1]);
      const steps = Math.max(1, Math.ceil(distance / 3));
      let previous = node(String(way.nodes[i]), a);
      for (let k = 1; k <= steps; k++) {
        const point = [a[0] + ((b[0] - a[0]) * k) / steps, a[1] + ((b[1] - a[1]) * k) / steps];
        const next = node(k === steps ? String(way.nodes[i + 1]) : `${way.id}:${i}:${k}`, point);
        nodes[previous].edges.push({ to: next, distance: distance / steps, way: w });
        nodes[next].edges.push({ to: previous, distance: distance / steps, way: w });
        previous = next;
      }
    }
  });
  return nodes;
}

const graphs = [graph(false), graph(true)];

export function routeTo(place, avoidSteps = false) {
  const mappedApproaches = {
    castle: [33.48, 172.16],
    spiral: [-24.62, 135.169],
    tickets: [-169.23, 164.13],
  };
  const nodes = graphs[avoidSteps ? 1 : 0];
  const target = mappedApproaches[place.id] ?? [
    (place.coordinates[0] + 0.78355) * 67084,
    (place.coordinates[1] - 52.89315) * 111195,
  ];
  const origin = [-169.23, 164.13];
  const result = {
    destination: place.id,
    points: [],
    metres: 0,
    minutes: 0,
    gap: 0,
    surfaces: [],
    unknownSurface: false,
    steps: false,
    slope: false,
  };
  function nearest(point) {
    let index = -1;
    let distance = Infinity;
    nodes.forEach((n, i) => {
      const d = Math.hypot(n.point[0] - point[0], n.point[1] - point[1]);
      if (d < distance) {
        index = i;
        distance = d;
      }
    });
    return { index, distance };
  }
  const start = nearest(origin);
  const end = nearest(target);
  result.gap = Math.round(end.distance);
  if (start.index < 0 || start.distance > 10 || end.distance > 30) {
    return { ...result, error: 'No mapped path reaches close enough to this place. Ask the visitor team for directions.' };
  }
  const distance = nodes.map(() => Infinity);
  const previous = nodes.map(() => -1);
  const usedWay = nodes.map(() => -1);
  const done = new Set();
  distance[start.index] = 0;
  while (done.size < nodes.length) {
    let current = -1;
    let best = Infinity;
    distance.forEach((d, i) => {
      if (!done.has(i) && d < best) {
        best = d;
        current = i;
      }
    });
    if (current < 0 || current === end.index) break;
    done.add(current);
    for (const edge of nodes[current].edges) {
      const d = best + edge.distance;
      if (d < distance[edge.to]) {
        distance[edge.to] = d;
        previous[edge.to] = current;
        usedWay[edge.to] = edge.way;
      }
    }
  }
  if (!Number.isFinite(distance[end.index])) {
    return { ...result, error: 'These paths are not connected in the available map. A route needs confirmation from the estate.' };
  }
  const ids = [];
  for (let i = end.index; i !== -1; i = previous[i]) ids.push(i);
  ids.reverse();
  result.points = ids.map((i) => nodes[i].point);
  result.metres = Math.round(distance[end.index]);
  result.minutes = Math.max(1, Math.ceil(result.metres / (4000 / 60)));
  const surfaces = new Set();
  for (const i of ids) {
    const w = usedWay[i];
    if (w < 0) continue;
    const tags = network.ways[w].tags || {};
    if (tags.surface) surfaces.add(String(tags.surface).replaceAll('_', ' '));
    else result.unknownSurface = true;
    if (tags.highway === 'steps') result.steps = true;
    if (tags.incline && tags.incline !== '0') result.slope = true;
  }
  result.surfaces = [...surfaces];
  return result;
}
